import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { addRegion, fetchAllRegions, updateRegion } from "../../HTTP/countries";
import { countries } from "../../global/countriesAndSources";

const getErrorMessage = (error, isEditing) => {
    if (error?.status === 400) return "Le nom ou la liste des pays est invalide.";
    if (error?.status === 404) return "Cette région n'existe pas.";
    if (error?.status === 409) return "Une région porte déjà ce nom.";
    if (error?.status === 403) return "Vous n'avez pas la permission de gérer les régions.";
    return error?.message || `La région n'a pas pu être ${isEditing ? "modifiée" : "créée"}.`;
};

export default function RegionForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = Boolean(id);
    const localUser = JSON.parse(localStorage.getItem("user") || "null");
    const hasRegionPermission = Boolean(localUser) &&
        (Number(localUser.super_admin) === 1 || Number(localUser.admin_access) === 5);
    const [name, setName] = useState("");
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [countrySearch, setCountrySearch] = useState("");
    const [loading, setLoading] = useState(isEditing);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const availableCountries = useMemo(
        () => countries.filter((country) => country.code !== "wd"),
        []
    );
    const filteredCountries = useMemo(() => {
        const normalizedSearch = countrySearch.trim().toLowerCase();
        if (!normalizedSearch) return availableCountries;
        return availableCountries.filter((country) =>
            country.name.toLowerCase().includes(normalizedSearch) ||
            country.code.toLowerCase().includes(normalizedSearch)
        );
    }, [availableCountries, countrySearch]);

    useEffect(() => {
        if (!hasRegionPermission) {
            window.location.href = "/";
        }
    }, [hasRegionPermission]);

    useEffect(() => {
        if (!isEditing) return undefined;
        let isMounted = true;

        fetchAllRegions()
            .then((response) => {
                const rows = Array.isArray(response) ? response : response?.data;
                const region = Array.isArray(rows)
                    ? rows.find((item) => String(item.id) === String(id))
                    : null;
                if (!region) {
                    const notFoundError = new Error("Cette région n'existe pas.");
                    notFoundError.status = 404;
                    throw notFoundError;
                }
                if (isMounted) {
                    setName(region.name || "");
                    setSelectedCodes((region.countries || [])
                        .map((item) => String(item.country_code || "").toUpperCase())
                        .filter(Boolean));
                }
            })
            .catch((loadError) => {
                console.error("Erreur lors du chargement de la région", loadError);
                if (isMounted) setError(getErrorMessage(loadError, true));
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [id, isEditing]);

    const toggleCountry = (code) => {
        setSelectedCodes((current) =>
            current.includes(code)
                ? current.filter((item) => item !== code)
                : [...current, code]
        );
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError("");
        setSaving(true);
        const payload = {
            name: name.trim(),
            countries: selectedCodes
        };

        try {
            if (isEditing) {
                await updateRegion({ id: Number(id), ...payload });
            } else {
                await addRegion(payload);
            }
            navigate("/admin/regions");
        } catch (saveError) {
            console.error("Erreur lors de l'enregistrement de la région", saveError);
            setError(getErrorMessage(saveError, isEditing));
        } finally {
            setSaving(false);
        }
    };

    return (
        <main className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
            <h1 className="mb-6 text-2xl font-bold">
                {isEditing ? "Modifier une région" : "Créer une région"}
            </h1>
            {error && <p role="alert" className="mb-4 rounded bg-red-100 p-3 text-red-800">{error}</p>}
            {loading ? (
                <p>Chargement de la région...</p>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6 rounded-lg bg-white p-5 shadow">
                    <div>
                        <label htmlFor="region-name" className="mb-2 block font-semibold">Nom de la région</label>
                        <input
                            id="region-name"
                            type="text"
                            required
                            maxLength={255}
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            className="w-full rounded border border-gray-300 px-3 py-2"
                        />
                    </div>

                    <fieldset>
                        <legend className="mb-2 font-semibold">Pays associés ({selectedCodes.length})</legend>
                        <input
                            type="search"
                            value={countrySearch}
                            onChange={(event) => setCountrySearch(event.target.value)}
                            placeholder="Filtrer les pays"
                            aria-label="Filtrer les pays"
                            className="mb-3 w-full rounded border border-gray-300 px-3 py-2"
                        />
                        <div className="grid max-h-96 grid-cols-1 gap-2 overflow-y-auto rounded border border-gray-200 p-3 sm:grid-cols-2 lg:grid-cols-3">
                            {filteredCountries.map((country) => {
                                const code = country.code.toUpperCase();
                                return (
                                    <label key={code} className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={selectedCodes.includes(code)}
                                            onChange={() => toggleCountry(code)}
                                        />
                                        <span>{country.name} ({code})</span>
                                    </label>
                                );
                            })}
                        </div>
                    </fieldset>

                    <div className="flex flex-wrap justify-end gap-3">
                        <button
                            type="button"
                            className="rounded border border-gray-300 px-4 py-2"
                            onClick={() => navigate("/admin/regions")}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="rounded bg-blue-600 px-4 py-2 font-semibold text-white disabled:opacity-60"
                        >
                            {saving ? "Enregistrement..." : "Enregistrer"}
                        </button>
                    </div>
                </form>
            )}
        </main>
    );
}
